import { cva, VariantProps } from 'class-variance-authority';
import classNames from 'classnames';
import { X, type LucideIcon } from 'lucide-react';
import { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../../utils/cn';

const badgeVariants = cva(
  'box-content font-medium inline-flex items-center rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus-within:outline-none gap-1.5',
  {
    variants: {
      variant: {
        primary: 'border-primary/15 bg-primary/10 text-primary',
        secondary: 'border-border bg-accent text-foreground',
        error: 'border-transparent bg-destructive-muted text-destructive',
        outline: 'border-border bg-transparent text-foreground',
        success: 'border-transparent bg-success-muted text-success',
        info: 'border-transparent bg-info-muted text-info',
        warning: '!border-transparent bg-warning-muted text-warning-foreground',
        custom: '',
      },
      size: {
        xs: 'h-3 px-1 py-px !text-xxs rounded-lg',
        sm: 'h-4 px-1 py-0.25 !text-xxs',
        md: 'h-3.5 px-2 py-1 text-xs',
        lg: 'h-4 px-2.5 py-2 text-xs',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type BadgeVariantProps = VariantProps<typeof badgeVariants>;

type BadgeProps = HTMLAttributes<HTMLDivElement> &
  BadgeVariantProps & {
    startIcon?: ReactNode;
    onClose?: () => void;
    closeIcon?: LucideIcon;
    disabled?: boolean;
  };

function Badge({
  className,
  variant,
  size,
  children,
  startIcon,
  onClose,
  disabled = false,
  closeIcon: CloseIcon = X,
  ...props
}: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {startIcon && (
        <div
          className={classNames('text-inherit font-[inherit]', {
            'border-r border-[inherit] mr-1 pr-1': !!children,
          })}
        >
          {startIcon}
        </div>
      )}
      {children}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          disabled={disabled}
          className="rounded-full bg-accent p-1 text-foreground hover:!bg-accent hover:cursor-pointer focus:outline-none disabled:cursor-auto"
        >
          <CloseIcon className="size-2.5" />
        </button>
      )}
    </div>
  );
}

export { Badge, badgeVariants };
